import Button from '@platform-ui-common/components/Button/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Message from '@platform-ui-common/components/Message/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import cx from 'classnames';
import { ReactNode, isValidElement } from 'react';

interface ClaimFormProps {
  header: string;
  children: ReactNode;
  onSubmit: () => void;
  onReset?: () => void;
  errors?: string[] | ReactNode;
  subHeader?: ReactNode;
  helperText?: ReactNode;
  isSubmit?: boolean;
  loading?: boolean;
}

const ErrorDisplay = ({ children }: { children: ReactNode }): JSX.Element => (
  <div className="max-w-[320px] w-full">
    <Message variant="warning">{children}</Message>
  </div>
);

function ClaimError({ errors }: Partial<ClaimFormProps>): JSX.Element {
  if (isValidElement(errors)) return <ErrorDisplay>{errors}</ErrorDisplay>;
  if (Array.isArray(errors) && errors?.length > 0)
    return <ErrorDisplay>{errors.join(',')}</ErrorDisplay>;
  return null;
}

function ClaimForm({
  header,
  subHeader,
  children,
  errors,
  helperText,
  onSubmit,
  isSubmit,
  onReset,
  loading,
}: ClaimFormProps): JSX.Element {
  return (
    <FormContainer
      title={header}
      buttons={
        <div className="flex flex-col gap-4 items-center">
          <ClaimError errors={errors} />
          <Button disabled={loading} loading={loading} type="submit">
            {isSubmit ? 'Submit' : 'Save And Continue'}
          </Button>
          {onReset && (
            <Button onClick={onReset} variant="outline">
              Select Other Payment Method
            </Button>
          )}
        </div>
      }
      onSubmit={onSubmit}
    >
      {subHeader && (
        <div className="lg:w-3/4 text-center pb-4">
          <Typography variant="largeBody">{subHeader}</Typography>
        </div>
      )}
      <div className="flex flex-col w-full items-center gap-y-4">{children}</div>
      {
        // Adding absolute position help escape this element from parent flex calculations when not visible
      }
      <div aria-live="polite" className={cx(!helperText && 'absolute')}>
        {!!helperText && (
          <div className="flex text-left gap-2 pt-6 pb-4 max-w-[320px] w-full items-start">
            {helperText}
          </div>
        )}
      </div>
    </FormContainer>
  );
}

export default ClaimForm;
